import { dist } from "./geometry";
import { drawRotateGrip, getBounds, HANDLE_TO_MAG_KEY, handleTargetPoint } from "./drawObjects";

function distToSegment(pt, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((pt.x - a.x) * dx + (pt.y - a.y) * dy) / len2));
  return dist(pt, { x: a.x + dx * t, y: a.y + dy * t });
}

export function toLocal(obj, pt) {
  const rot = obj.type === "text" ? Math.atan2(obj.y2 - obj.y1, obj.x2 - obj.x1) * 180 / Math.PI : (obj.rotation || 0);
  if (!rot) return pt;

  let cx, cy;
  if (obj.type === "text") {
    cx = obj.x1;
    cy = obj.y1;
  } else {
    const b = getBounds(obj);
    if (!b) return pt;
    cx = b.x + b.w / 2;
    cy = b.y + b.h / 2;
  }

  const rad = -rot * Math.PI / 180;
  const dx = pt.x - cx;
  const dy = pt.y - cy;

  return {
    x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
    y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
  };
}

export function localToWorld(obj, localPt) {
  const rot = obj.type === "text" ? Math.atan2(obj.y2 - obj.y1, obj.x2 - obj.x1) * 180 / Math.PI : (obj.rotation || 0);
  if (!rot) return localPt;

  let cx, cy;
  if (obj.type === "text") {
    cx = obj.x1;
    cy = obj.y1;
  } else {
    const b = getBounds(obj);
    if (!b) return localPt;
    cx = b.x + b.w / 2;
    cy = b.y + b.h / 2;
  }

  const rad = rot * Math.PI / 180;
  const dx = localPt.x - cx;
  const dy = localPt.y - cy;

  return {
    x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
    y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
  };
}

export function getToolbarAnchor(obj) {
  const b = getBounds(obj);
  if (!b) return { x: 100, y: 100 };

  const corners = [
    { x: b.x, y: b.y },
    { x: b.x + b.w, y: b.y },
    { x: b.x, y: b.y + b.h },
    { x: b.x + b.w, y: b.y + b.h },
  ];
  const world = corners.map(c => localToWorld(obj, c));
  const maxY = Math.max(...world.map(p => p.y));
  const avgX = world.reduce((s, p) => s + p.x, 0) / 4;

  return { x: avgX, y: maxY + 14 };
}

export function getWorldCorners(obj) {
  const b = getBounds(obj);
  if (!b) return [];

  const corners = [
    { x: b.x, y: b.y },
    { x: b.x + b.w, y: b.y },
    { x: b.x, y: b.y + b.h },
    { x: b.x + b.w, y: b.y + b.h },
  ];

  return corners.map(c => localToWorld(obj, c));
}

export function getMultiBounds(objs) {
  if (!objs.length) return null;

  let pts = [];
  objs.forEach(o => { pts = pts.concat(getWorldCorners(o)); });
  if (!pts.length) return null;

  const xs = pts.map(p => p.x);
  const ys = pts.map(p => p.y);

  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    w: Math.max(...xs) - Math.min(...xs),
    h: Math.max(...ys) - Math.min(...ys),
  };
}

export function drawMultiSelection(ctx, sels) {
  const b = getMultiBounds(sels);
  if (!b) return;

  ctx.save();
  ctx.strokeStyle = "#007AFF";
  ctx.lineWidth = 1.5;
  ctx.setLineDash([6, 4]);
  ctx.strokeRect(b.x - 6, b.y - 6, b.w + 12, b.h + 12);
  ctx.setLineDash([]);
  const cx = b.x + b.w / 2;
  ctx.beginPath();
  ctx.moveTo(cx, b.y - 6);
  ctx.lineTo(cx, b.y - 32);
  ctx.strokeStyle = "rgba(255,255,255,0.85)";
  ctx.lineWidth = 1.5;
  ctx.stroke();
  drawRotateGrip(ctx, cx, b.y - 32, "#007AFF");
  ctx.restore();
}

export function getMultiToolbarAnchor(sels) {
  const b = getMultiBounds(sels);
  if (!b) return { x: 100, y: 100 };

  return { x: b.x + b.w / 2, y: b.y + b.h + 6 + 14 };
}

export function clampAnchor(anchor, imgSize, w = 116, h = 40) {
  let x = Math.min(Math.max(anchor.x, w / 2 + 4), Math.max(w / 2 + 4, imgSize.w - w / 2 - 4));
  let y = anchor.y;

  if (y + h + 8 > imgSize.h) y = Math.max(8, imgSize.h - h - 8);
  if (Number.isNaN(x)) x = imgSize.w / 2;

  return { x, y };
}

export function hitHandle(obj, pt) {
  if (obj.type === "magnifier") {
    const r = obj.lensRadius || 70;
    if (dist(pt, { x: obj.anchorX, y: obj.anchorY }) < 14) return "magnifier-anchor";
    if (dist(pt, { x: obj.targetX, y: obj.targetY }) < 14) return "magnifier-target";
    if (dist(pt, { x: obj.lensX + r, y: obj.lensY }) < 14) return "magnifier-radius";
    if (dist(pt, { x: obj.lensX, y: obj.lensY }) < r) return "magnifier-lens";
    return null;
  }

  if (obj.type === "timestamp" || obj.type === "sticker") {
    const b = getBounds(obj);
    if (b) {
      const lp = toLocal(obj, pt);
      if (dist(lp, { x: b.x, y: b.y }) < 10) return "tl";
      if (dist(lp, { x: b.x + b.w, y: b.y }) < 10) return "tr";
      if (dist(lp, { x: b.x, y: b.y + b.h }) < 10) return "bl";
      if (dist(lp, { x: b.x + b.w, y: b.y + b.h }) < 10) return "br";
    }
    return null;
  }

  if (!["arrow", "line", "rect", "circle", "text", "blur", "spotlight"].includes(obj.type)) return null;

  // ── Check embedded magnifier lens handles first (highest priority) ─────────
  // "p1-lens-radius", "p2-lens-radius" etc. drag the lens size handle (right edge)
  // "p1-lens", "p2-lens" etc. drag the lens bubble itself
  for (const [handleKey, magKey] of Object.entries(HANDLE_TO_MAG_KEY)) {
    const mag = obj[magKey];
    if (!mag) continue;
    const r = mag.lensRadius || 70;
    if (dist(pt, { x: mag.lensX + r, y: mag.lensY }) < 12) return `${handleKey}-lens-radius`;
    if (dist(pt, { x: mag.lensX, y: mag.lensY }) < r) return `${handleKey}-lens`;
  }

  const b = getBounds(obj);
  if (!b) return null;

  const lp = toLocal(obj, pt);
  if (obj.type === "arrow" || obj.type === "line" || obj.type === "text") {
    if (dist(pt, { x: obj.x1, y: obj.y1 }) < 12) return "p1";
    if (dist(pt, { x: obj.x2, y: obj.y2 }) < 12) return "p2";
    return null;
  }

  // rect/circle corners (use raw coords so bounds don't include lens)
  const rx1 = Math.min(obj.x1, obj.x2), ry1 = Math.min(obj.y1, obj.y2);
  const rx2 = Math.max(obj.x1, obj.x2), ry2 = Math.max(obj.y1, obj.y2);
  const corners = [
    { key: "tl", x: rx1, y: ry1 },
    { key: "tr", x: rx2, y: ry1 },
    { key: "bl", x: rx1, y: ry2 },
    { key: "br", x: rx2, y: ry2 },
  ];

  for (const c of corners) {
    if (dist(lp, c) < 10) return c.key;
  }

  return null;
}

export function hitObject(obj, pt) {
  if (obj.type === "magnifier") {
    const r = obj.lensRadius || 70;
    if (dist(pt, { x: obj.lensX, y: obj.lensY }) <= r + 8) return true;
    if (dist(pt, { x: obj.anchorX, y: obj.anchorY }) <= 12) return true;
    if (dist(pt, { x: obj.targetX, y: obj.targetY }) <= 12) return true;
    const dx = obj.lensX - obj.anchorX;
    const dy = obj.lensY - obj.anchorY;
    const len = Math.hypot(dx, dy) || 1;
    const edge = { x: obj.lensX - (dx / len) * r, y: obj.lensY - (dy / len) * r };
    if (distToSegment(pt, { x: obj.anchorX, y: obj.anchorY }, edge) <= Math.max(10, (obj.thickness || 5) + 6)) return true;
  }

  // Also hit-test embedded lens bubbles
  if (["arrow", "line", "rect", "circle", "blur", "spotlight"].includes(obj.type)) {
    for (const [, magKey] of Object.entries(HANDLE_TO_MAG_KEY)) {
      const mag = obj[magKey];
      if (!mag) continue;
      const r = mag.lensRadius || 70;
      if (dist(pt, { x: mag.lensX, y: mag.lensY }) <= r + 8) return true;
    }
  }

  const b = getBounds(obj);
  if (!b) return false;

  const lp = toLocal(obj, pt);

  return lp.x >= b.x && lp.x <= b.x + b.w && lp.y >= b.y && lp.y <= b.y + b.h;
}
